/**
 * Import Bulk Modal
 *
 * Dialog for bulk importing layers from JSON.
 * Part of Layer Drawer 2.0 - Issue #254 (Layer Catalog & Metadata).
 *
 * Features:
 * - Textarea for pasting JSON array of layer definitions
 * - File upload button (alternative to paste)
 * - Preview table showing layers to import (name, type, group)
 * - Validation before import (check JSON structure)
 * - Import button triggers POST to /api/layers/bulk
 * - Progress/result display (created/skipped/errors)
 */

import React, { useState, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faRotate, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { LayerService } from '../../../../services/layerService';
import { CreateLayerInput, BulkCreateResult } from '../../../../types/layer';
import { LAYER_TYPES } from '../../../../shared/layerTypes';
import './ImportBulkModal.scss';
import AppModal from '../../../common/AppModal';

interface ImportBulkModalProps {
  onClose: (success: boolean) => void;
}

interface ValidationResult {
  valid: boolean;
  layers: CreateLayerInput[];
  errors: string[];
}

export const ImportBulkModal: React.FC<ImportBulkModalProps> = ({ onClose }) => {
  const layerService = new LayerService();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [jsonInput, setJsonInput] = useState('');
  const [parsedLayers, setParsedLayers] = useState<CreateLayerInput[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<BulkCreateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [overwrite, setOverwrite] = useState(false);

  /**
   * Validate JSON input and parse layers
   */
  const validateAndParse = useCallback((input: string): ValidationResult => {
    const errors: string[] = [];
    let layers: CreateLayerInput[] = [];

    if (!input.trim()) {
      return { valid: false, layers: [], errors: ['No JSON input provided'] };
    }

    try {
      const parsed = JSON.parse(input);

      // Handle both array format and { layers: [] } format
      if (Array.isArray(parsed)) {
        layers = parsed;
      } else if (parsed.layers && Array.isArray(parsed.layers)) {
        layers = parsed.layers;
      } else {
        errors.push('JSON must be an array of layers or an object with a "layers" array');
        return { valid: false, layers: [], errors };
      }

      if (layers.length === 0) {
        errors.push('No layers found in JSON');
        return { valid: false, layers: [], errors };
      }

      // Validate each layer has required fields
      layers.forEach((layer, index) => {
        if (!layer.name) {
          errors.push(`Layer ${index + 1}: Missing required field "name"`);
        }
        if (!layer.type) {
          errors.push(`Layer ${index + 1} (${layer.name || 'unnamed'}): Missing required field "type"`);
        }
        if (!layer.category) {
          errors.push(`Layer ${index + 1} (${layer.name || 'unnamed'}): Missing required field "category"`);
        }

        // Validate type values against the shared LAYER_TYPES constant
        // (kept in sync with the DB layers_type_check constraint by a drift test).
        if (layer.type && !(LAYER_TYPES as readonly string[]).includes(layer.type)) {
          errors.push(`Layer "${layer.name}": Invalid type "${layer.type}". Must be one of: ${LAYER_TYPES.join(', ')}`);
        }

        // Validate category values
        const validCategories = ['backgrounds', 'overlays', 'live'];
        if (layer.category && !validCategories.includes(layer.category)) {
          errors.push(`Layer "${layer.name}": Invalid category "${layer.category}". Must be one of: ${validCategories.join(', ')}`);
        }
      });

      return {
        valid: errors.length === 0,
        layers,
        errors,
      };
    } catch (parseError: any) {
      errors.push(`Invalid JSON: ${parseError.message}`);
      return { valid: false, layers: [], errors };
    }
  }, []);

  /**
   * Handle JSON input change
   */
  const handleJsonChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = e.target.value;
    setJsonInput(input);
    setImportResult(null);
    setError(null);

    if (input.trim()) {
      const result = validateAndParse(input);
      setParsedLayers(result.layers);
      setValidationErrors(result.errors);
    } else {
      setParsedLayers([]);
      setValidationErrors([]);
    }
  }, [validateAndParse]);

  /**
   * Handle file upload
   */
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setJsonInput(content);
      setImportResult(null);
      setError(null);

      const result = validateAndParse(content);
      setParsedLayers(result.layers);
      setValidationErrors(result.errors);
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [validateAndParse]);

  /**
   * Handle import button click
   */
  const handleImport = async () => {
    if (parsedLayers.length === 0 || validationErrors.length > 0) {
      return;
    }

    setImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const result = await layerService.bulkCreateLayers(parsedLayers, { overwrite });
      setImportResult(result);

      // If all successful, close modal after delay
      if (result.errors.length === 0 && (result.created > 0 || result.updated > 0)) {
        setTimeout(() => {
          onClose(true);
        }, 2000);
      }
    } catch (err: any) {
      console.error('[ImportBulkModal] Error importing layers:', err);
      setError(err.message || 'Failed to import layers');
    } finally {
      setImporting(false);
    }
  };

  /**
   * Trigger file input click
   */
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const isValid = parsedLayers.length > 0 && validationErrors.length === 0;

  return (
    <AppModal
      isOpen
      onClose={() => onClose(false)}
      width={900}
      maxHeight="90vh"
      header={<h2 className="modal-title">Bulk Import Layers</h2>}
    >
      <div className="import-bulk-modal">
        {/* Error Message */}
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className={`import-result ${importResult.errors.length > 0 ? 'has-errors' : 'success'}`}>
            <div className="result-summary">
              <span className="result-icon">
                {importResult.errors.length === 0 ? (
                  <FontAwesomeIcon icon={faCheck} />
                ) : (
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                )}
              </span>
              <span className="result-text">
                <strong>{importResult.created}</strong> created,{' '}
                <strong>{importResult.updated}</strong> updated,{' '}
                <strong>{importResult.skipped}</strong> skipped
                {importResult.errors.length > 0 && (
                  <>, <strong className="error-count">{importResult.errors.length}</strong> errors</>
                )}
              </span>
            </div>
            {importResult.errors.length > 0 && (
              <div className="result-errors">
                <h4>Errors:</h4>
                <ul>
                  {importResult.errors.map((err, index) => (
                    <li key={index}>
                      <strong>{err.name}:</strong> {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Input Section */}
        <div className="input-section">
          <div className="input-header">
            <label htmlFor="json-input">Paste JSON Layer Definitions</label>
            <div className="input-actions">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleUploadClick}
              >
                <FontAwesomeIcon icon={faUpload} /> Upload JSON File
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>
          <textarea
            id="json-input"
            className={`json-textarea ${validationErrors.length > 0 ? 'has-errors' : ''}`}
            placeholder={`Paste JSON array of layer definitions, e.g.:
[
  {
    "name": "Layer Name",
    "type": "wms",
    "category": "overlays",
    "layer_group": "Group Name",
    "source_url": "https://...",
    "config": { ... },
    "metadata": { ... }
  }
]`}
            value={jsonInput}
            onChange={handleJsonChange}
            rows={10}
          />
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="validation-errors">
            <h4>Validation Errors:</h4>
            <ul>
              {validationErrors.map((err, index) => (
                <li key={index}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview Table */}
        {parsedLayers.length > 0 && validationErrors.length === 0 && (
          <div className="preview-section">
            <h3>Preview ({parsedLayers.length} layers)</h3>
            <div className="preview-table-container">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Group</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedLayers.map((layer, index) => (
                    <tr key={index}>
                      <td className="layer-name">{layer.name}</td>
                      <td>
                        <span className={`type-badge type-${layer.type}`}>
                          {layer.type.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className={`category-badge category-${layer.category}`}>
                          {layer.category}
                        </span>
                      </td>
                      <td className="layer-group">{layer.layer_group || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Overwrite Toggle */}
        {parsedLayers.length > 0 && validationErrors.length === 0 && (
          <div className="overwrite-toggle">
            <label>
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
              />
              <span>
                <strong>Overwrite existing layers</strong> — when an imported layer's name
                matches an existing one, update it in place instead of skipping it.
              </span>
            </label>
          </div>
        )}

        {/* Action Buttons */}
        <div className="modal-actions">
          <button
            onClick={() => onClose(importResult?.created ? true : false)}
            className="btn btn-secondary"
            disabled={importing}
          >
            {importResult?.created ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleImport}
            className="btn btn-primary"
            disabled={importing || !isValid}
          >
            {importing ? (
              <>
                <FontAwesomeIcon icon={faRotate} spin /> Importing...
              </>
            ) : (
              `Import ${parsedLayers.length} Layer${parsedLayers.length !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      </div>
    </AppModal>
  );
};
